import { assert, expr, object } from '@codegrade/utils';
/**
 * Create a LTI 1p1 provider maker.
 *
 * @param name The name to use, if `tag` is not specified this must be of type Y too.
 * @param root0 Extra override data, see the object itself for docs.
 * @returns A new LTI 1.1 provider maker.
 */
function makeLTI1p1Provider(name, { tag = null, 
/** @returns By default no data is overriden. */
getOverride = () => null, } = {}) {
    const base = Object.freeze({
        tag: (tag ?? name),
        lms: name,
        version: 'lti1.1',
    });
    const override = getOverride();
    return Object.freeze({
        ...base,
        make: data => Object.freeze({
            addBorder: false,
            supportsDeadline: false,
            supportsBonusPoints: false,
            supportsStateManagement: false,
            supportsLockDate: data.supportsLockDate,
            supportsAvailableAtManagement: false,
            supportsName: true,
            supportsSettingMaxAttempts: false,
            ...base,
            ...override,
        }),
    });
}
const blackboardProvider = makeLTI1p1Provider('Blackboard');
const brightSpaceProvider = makeLTI1p1Provider('Brightspace', 
// In the backend we call Brightspace "BrightSpace" (with a capital S)
// so we need to override the tag that is used to identify the correct
// ltiProvider.
{ tag: 'BrightSpace' });
const moodleProvider = makeLTI1p1Provider('Moodle');
const sakaiProvider = makeLTI1p1Provider('Sakai');
const openEdxProvider = makeLTI1p1Provider('Open edX');
const canvasProvider = makeLTI1p1Provider('Canvas', {
    getOverride: () => ({
        addBorder: true,
        supportsDeadline: true,
        supportsBonusPoints: true,
        supportsStateManagement: true,
        supportsAvailableAtManagement: true,
    }),
});
const populiProvider = makeLTI1p1Provider('Populi');
const thoughtIndustriesProvider = makeLTI1p1Provider('Thought Industries', {
    getOverride: () => ({
        addBorder: true,
    }),
});
const defaultLTI1p1Provider = makeLTI1p1Provider('LMS');
/** All known LTI 1.3 provider names. */
export const LTI1p3ProviderNames = Object.freeze([
    'Blackboard',
    'Brightspace',
    'Canvas',
    'Moodle',
    'Revel',
    'XL',
    'Bronte',
]);
if (!IS_PRODUCTION) {
    // Assertion to make sure we list all LTI 1.3 LMS options above.
    assert.AssertionError.typeEqual('correct');
}
/** The capabilities for a LTI 1.3 provider. */
class LTI1p3ProviderCapabilties {
    lms;
    /** @inheritdoc */
    addBorder;
    /** Does this provider provide us with deadlines. */
    /** @inheritdoc */
    supportsDeadline;
    /** @inheritdoc */
    supportsLockDate;
    /** @inheritdoc */
    supportsBonusPoints;
    /** @inheritdoc */
    supportsStateManagement;
    /** @inheritdoc */
    supportsAvailableAtManagement;
    /** @inheritdoc */
    supportsName;
    /** @inheritdoc */
    supportsSettingMaxAttempts;
    /** This is always version 1.3. */
    version = 'lti1.3';
    /**
     * @param lms The LMS we create the capabilities for.
     * @param capabilities The custom capabilities of the LMS.
     */
    constructor(lms, capabilities) {
        this.lms = lms;
        this.addBorder = lms === 'Canvas';
        this.supportsDeadline = !capabilities.setDeadline;
        this.supportsBonusPoints = true;
        this.supportsStateManagement = !capabilities.setState;
        this.supportsLockDate = !capabilities.setLockDate;
        this.supportsAvailableAtManagement = !capabilities.setAvailableAt;
        this.supportsName = !capabilities.setName;
        this.supportsSettingMaxAttempts = !capabilities.setMaxAttempts;
    }
}
/** All possible LTI 1.1 provider makers. */
export const LTI1p1Providers = [
    blackboardProvider,
    brightSpaceProvider,
    canvasProvider,
    moodleProvider,
    sakaiProvider,
    openEdxProvider,
    populiProvider,
    thoughtIndustriesProvider,
];
/** A lookup between LMS name and LTI 1.1 provider maker. */
export const LTI1p1Lookup = object.mapToObject(LTI1p1Providers, prov => [prov.tag, prov]);
/**
 * Create a new LTI provider from API data.
 *
 * @param provider The api data to use.
 * @returns The created LTI provider.
 */
export function makeProvider(provider) {
    return expr.match(provider, 'version', {
        'lti1.1': prov => (LTI1p1Lookup[prov.lms] ?? defaultLTI1p1Provider).make({
            supportsLockDate: prov.supportsLockDate,
        }),
        'lti1.3': prov => new LTI1p3ProviderCapabilties(prov.lms, prov.capabilities),
    });
}
